import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
// Direct import from the library (not the local _Elements/selection barrel)
// to avoid a dependency cycle: the barrel re-exports this very component.
// eslint-disable-next-line import/no-internal-modules
import { PillsListBox } from '@askyourteam-nz/react-library/dist/elements/selection';
import { SVGArrowSolid } from '_Icons';
import { SYSTEM_COLOR } from '_Lib/Enum';
import './PinnedScrollingPillRow.scss';

// Pixels to scroll per chevron click. Sized to advance roughly one
// pill width plus its gap, so a click moves the row visibly without
// hopping a whole viewport.
const CHEVRON_SCROLL_STEP = 200;

// Validates `dataComponent` so the value is safe to splice into
// data-cy attributes (which Cypress queries by raw string). A bad
// value would silently produce unselectable testids.
const VALID_DATA_COMPONENT = /^[a-z][a-z0-9-]*$/;

// YouTube-style horizontal pill row with a sticky pinned region on
// the left and a horizontally-scrolling region on the right. Chevron
// buttons appear conditionally based on scroll position to telegraph
// overflow and let users advance the row without using the scrollbar.
//
// Layout (single physical row that overflows horizontally inside the
// scroll region, with the right chevron flowing as a flex sibling
// AFTER the scroll region):
//   [pinned pills | <chevron-left> | scrolling pills... ]  <chevron-right>
//
// Behaviour:
//   - Left chevron renders inside the sticky region so it sits at the
//     right edge of the pinned group (after the | divider), not at the
//     absolute left of the page where it would overlap the pinned pills.
//   - Right chevron is a flex sibling of the scroll region. The scroll
//     region grows to fill available width and the chevron occupies its
//     own column on the right. This prevents the chevron from
//     overlapping the rightmost pill (the prior absolute-positioned
//     implementation would float over the last visible pill).
//   - Both chevrons only render when there's overflow to scroll in
//     that direction. A ResizeObserver observes the scroll container
//     AND all its children so late-arriving options (async fetches,
//     font load, content edits that keep array length the same) still
//     trigger correct chevron state.
//   - Chevrons are tabIndex=-1 because keyboard users navigate the
//     listbox via arrow keys (handled by PillsListBox); the chevrons
//     are pointer-only affordances and don't need to be in the tab
//     order.
//
// Both pill regions share a single `value` and `onChange` so the
// caller treats them as one logical pill list. The component wraps
// them in role="group" with `ariaLabel` so screen readers hear one
// logical unit rather than two separate listboxes.
const PinnedScrollingPillRow = (props) => {
  const {
    value,
    pinnedOptions,
    scrollingOptions,
    ariaLabel,
    onChange,
    dataComponent = 'pinned-scrolling-pill-row'
  } = props;

  const scrollContainerRef = useRef(null);
  const [showLeftChevron, setShowLeftChevron] = useState(false);
  const [showRightChevron, setShowRightChevron] = useState(false);

  const updateChevronVisibility = useCallback(
    () => {
      const el = scrollContainerRef.current;
      if (!el) return;
      // -1px tolerance on the right-edge comparison because browsers
      // can report sub-pixel rounding when the layout fits exactly.
      setShowLeftChevron(el.scrollLeft > 0);
      setShowRightChevron(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    },
    []
  );

  useEffect(
    () => {
      const el = scrollContainerRef.current;
      if (!el) return undefined;

      // requestAnimationFrame defers the first measurement until
      // after the browser has completed layout of freshly-mounted
      // children. A synchronous read at this point can return
      // scrollWidth == clientWidth (no overflow detected) and stick
      // showRightChevron at false until the next scroll or resize.
      const rafId = requestAnimationFrame(updateChevronVisibility);

      el.addEventListener('scroll', updateChevronVisibility, { passive: true });

      const resizeObserver = typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateChevronVisibility)
        : null;
      if (resizeObserver) {
        resizeObserver.observe(el);
        // Observe every child of the scroll container so a content-
        // size change anywhere inside (pinned region growing, scrolling
        // pills being added or renamed) triggers a re-measure. Without
        // this, content changes that don't alter the array length
        // would leave chevron state stale.
        Array.from(el.children).forEach((child) => resizeObserver.observe(child));
      }
      window.addEventListener('resize', updateChevronVisibility);

      return () => {
        cancelAnimationFrame(rafId);
        el.removeEventListener('scroll', updateChevronVisibility);
        window.removeEventListener('resize', updateChevronVisibility);
        if (resizeObserver) resizeObserver.disconnect();
      };
    },
    [updateChevronVisibility, pinnedOptions.length, scrollingOptions.length]
  );

  const scrollBy = useCallback(
    amount => {
      scrollContainerRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
    },
    []
  );

  return (
    <div
      className={'pinned-scrolling-pill-row'}
      data-component={dataComponent}
      role={'group'}
      aria-label={ariaLabel}
    >
      <div
        ref={scrollContainerRef}
        className={'pinned-scrolling-pill-row__scroll'}
        data-component={`${dataComponent}__scroll`}
      >
        {/*
          Pinned sticky region: contains the pinned pills AND the
          left chevron. Left chevron renders inside the sticky region
          so it sits at the right edge of the pinned group (after the
          | divider), not at the absolute left of the page where it
          would overlap the first pinned pill.
        */}
        <div
          className={'pinned-scrolling-pill-row__sticky-left'}
          data-component={`${dataComponent}__pinned-region`}
        >
          <div className={'pinned-scrolling-pill-row__pinned'}>
            <PillsListBox
              value={value}
              options={pinnedOptions}
              onChange={onChange}
              aria-label={`${ariaLabel} (pinned)`}
            />
          </div>
          {showLeftChevron && (
            <button
              type={'button'}
              tabIndex={-1}
              aria-label={'Scroll pill row left'}
              aria-hidden={'true'}
              className={'pinned-scrolling-pill-row__chevron pinned-scrolling-pill-row__chevron--left'}
              onClick={() => scrollBy(-CHEVRON_SCROLL_STEP)}
              data-cy={`${dataComponent}-chevron-left`}
              data-component={`${dataComponent}__chevron-left`}
            >
              <SVGArrowSolid size={12} rotate={90} color={SYSTEM_COLOR.PRIMARY} />
            </button>
          )}
        </div>
        {scrollingOptions.length > 0 && (
          <PillsListBox
            value={value}
            options={scrollingOptions}
            onChange={onChange}
            aria-label={`${ariaLabel} (scrolling)`}
          />
        )}
      </div>
      {showRightChevron && (
        <button
          type={'button'}
          tabIndex={-1}
          aria-label={'Scroll pill row right'}
          aria-hidden={'true'}
          className={'pinned-scrolling-pill-row__chevron pinned-scrolling-pill-row__chevron--right'}
          onClick={() => scrollBy(CHEVRON_SCROLL_STEP)}
          data-cy={`${dataComponent}-chevron-right`}
          data-component={`${dataComponent}__chevron-right`}
        >
          <SVGArrowSolid size={12} rotate={270} color={SYSTEM_COLOR.PRIMARY} />
        </button>
      )}
    </div>
  );
};

PinnedScrollingPillRow.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  pinnedOptions: PropTypes.array.isRequired,
  scrollingOptions: PropTypes.array.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  // Lower-case kebab so it splices safely into data-cy / data-component.
  // Invalid values would silently produce unselectable Cypress targets.
  dataComponent: (props, propName, componentName) => {
    const value = props[propName];
    if (value === undefined) return null;
    if (typeof value !== 'string' || !VALID_DATA_COMPONENT.test(value)) {
      return new Error(
        `Invalid prop \`${propName}\` supplied to \`${componentName}\`: ` +
        `expected lowercase kebab-case (matching ${VALID_DATA_COMPONENT}), got "${value}".`
      );
    }
    return null;
  }
};

export default PinnedScrollingPillRow;
